import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/translations';
import { Target, Eye, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const AboutSection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      badge: 'À PROPOS',
      title: 'KEYAN EXPERTISE',
      subtitle: 'Votre partenaire de confiance',
      description: "KEYAN EXPERTISE est une société polyvalente qui propose une gamme complète de services dans les domaines du Bâtiment, de l'Immobilier et des Services Techniques. Notre engagement est de garantir des prestations de haute qualité, soutenues par une équipe de professionnels expérimentés, pour assurer la satisfaction totale de nos clients.",
      activities: {
        title: 'Nos domaines d\'intervention',
        description: "KEYAN EXPERTISE se positionne comme un acteur majeur grâce à la diversité et la complémentarité de ses métiers. Nous offrons une solution globale pour tous vos projets."
      },
      vision: {
        title: 'Notre Vision',
        items: [
          { title: 'Expertise', desc: 'Équipe qualifiée et expérimentée' },
          { title: 'Qualité', desc: 'Matériaux certifiés, normes respectées' },
          { title: 'Satisfaction', desc: 'Accompagnement personnalisé' },
          { title: 'Fiabilité', desc: 'Respect des délais et engagements' }
        ]
      },
      mission: {
        title: 'Notre Mission',
        description: "Concevoir, construire et entretenir des infrastructures fiables et modernes, en garantissant qualité, sécurité et satisfaction totale de nos clients, tout en favorisant l'expertise locale et l'innovation dans chacun de nos projets."
      },
      cta: 'Voir nos services'
    },
    en: {
      badge: 'ABOUT US',
      title: 'KEYAN EXPERTISE',
      subtitle: 'Your trusted partner',
      description: "KEYAN EXPERTISE is a versatile company offering a complete range of services in the fields of Building, Real Estate and Technical Services. Our commitment is to guarantee high-quality services, supported by a team of experienced professionals, to ensure the total satisfaction of our clients.",
      activities: {
        title: 'Our areas of intervention',
        description: "KEYAN EXPERTISE positions itself as a major player thanks to the diversity and complementarity of its trades. We offer a global solution for all your projects."
      },
      vision: {
        title: 'Our Vision',
        items: [
          { title: 'Expertise', desc: 'Qualified and experienced team' },
          { title: 'Quality', desc: 'Certified materials, standards respected' },
          { title: 'Satisfaction', desc: 'Personalized support' },
          { title: 'Reliability', desc: 'Respect for deadlines and commitments' }
        ]
      },
      mission: {
        title: 'Our Mission',
        description: "To design, build and maintain reliable and modern infrastructure, guaranteeing quality, safety and total satisfaction of our clients, while promoting local expertise and innovation in each of our projects."
      },
      cta: 'View our services'
    }
  };

  const t = content[language];

  const scrollToServices = () => {
    const element = document.querySelector('#services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A227]/10 border border-[#C9A227]/20 rounded-full mb-6">
            <span className="text-[#C9A227] font-semibold text-sm">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-[#C9A227] font-medium mb-8">{t.subtitle}</p>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* Two columns: Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          
          {/* Vision */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#C9A227] rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A1A1A]">{t.vision.title}</h3>
            </div>
            <div className="space-y-4">
              {t.vision.items.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-[#1A1A1A]">{item.title}</span>
                    <span className="text-gray-600"> — {item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission */}
          <div className="bg-[#1A1A1A] rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#C9A227] rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{t.mission.title}</h3>
            </div>
            <p className="text-gray-300 leading-relaxed">
              {t.mission.description}
            </p>
          </div>
        </div>

        {/* Activities intro + CTA */}
        <div className="text-center bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100">
          <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4">{t.activities.title}</h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            {t.activities.description}
          </p>
          <Button
            onClick={scrollToServices}
            className="bg-[#C9A227] hover:bg-[#B8922A] text-white px-8 py-6 text-lg group"
          >
            {t.cta}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        {/* Contact info */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-gray-500 text-sm">
          <span>{companyInfo.phones[0]}</span>
          <span>{companyInfo.email}</span>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;